/**
 * HTTP client for sv_gateway.
 *
 * Two rules carry the weight here:
 *
 * 1. ONE refresh at a time. The gateway ROTATES refresh tokens — POST /auth/refresh
 *    hands back a new refresh_token and retires the one it was given. So if two
 *    calls both hit 401 and each starts its own refresh, the second presents a token
 *    the first already burned, the server rejects it, and the user is thrown back to
 *    the login screen for no reason. Concurrent 401s therefore await the SAME
 *    in-flight refresh promise, then each retries its own request once.
 *
 * 2. X-Company-Id is a scope, never an identity. requestControl() must send it
 *    (sv_control is multi-tenant and keys everything off it); requestMe() must send
 *    it only when the caller is filtering, because /me/* takes the caller's identity
 *    from the JWT (see sv_gateway/src/handlers/me.rs) and a header we invented would
 *    silently narrow the result to a company the user never chose.
 */
import {API_BASE_URL, SSO_BASE_URL} from '@/shared/config/env';
import {SESSION_IN_COOKIES} from '@/shared/api/session-mode';
import {clearTokens, loadTokens, saveTokens, type Tokens} from '@/shared/api/tokens';

export type {Tokens} from '@/shared/api/tokens';
export {clearTokens, loadTokens, saveTokens} from '@/shared/api/tokens';

export class ApiError extends Error {
  readonly status: number;
  readonly body: unknown;

  constructor(status: number, message: string, body: unknown) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.body = body;
  }
}

/**
 * Stands in for a token pair in cookie mode, where the credential exists but is
 * not something this code is allowed to see. Not a session — just "the call
 * succeeded and there is nothing to hand back".
 */
const EMPTY_TOKENS: Tokens = {accessToken: '', refreshToken: ''};

/**
 * Asks the gateway to put the session in HttpOnly cookies rather than the
 * response body. Absent on native, where the response is unchanged from what it
 * has always been — which is what keeps this path invisible to the app.
 */
function sessionModeHeader(): Record<string, string> {
  return SESSION_IN_COOKIES ? {'X-Session-Mode': 'cookie'} : {};
}

/**
 * `same-origin` is already fetch's default in a browser, but stating it means a
 * future move of the API to another host fails loudly (no cookie sent) instead
 * of silently widening to `include` and shipping credentials cross-origin.
 */
function credentialsInit(): RequestInit {
  return SESSION_IN_COOKIES ? {credentials: 'same-origin'} : {};
}

/** In-memory mirror of the Keychain, so the hot path is not an async native call. */
let cachedTokens: Tokens | null = null;

/** Non-null exactly while a refresh is in flight. This IS the de-duplication. */
let refreshInFlight: Promise<Tokens> | null = null;

let unauthorizedHandler: (() => void) | null = null;

let mustChangePasswordHandler: (() => void) | null = null;

/** Called when the session is gone for good (the refresh itself was rejected). */
export function onUnauthorized(cb: () => void): void {
  unauthorizedHandler = cb;
}

/**
 * Called when a REFRESH comes back flagged `must_change_password`.
 *
 * The flag rides on every issue of the token pair, not just the first, and the docs
 * require reading it in both places. A refresh is what keeps a session alive across
 * the access token's hour, so a client that reads the flag only at login loses the
 * requirement at the first rotation — the person signs in, is sent here, waits out an
 * hour without submitting, and the next refresh silently lets them through.
 *
 * (It is NOT the path by which an already-running session first learns of a reset:
 * `POST /users/{id}/reset-password` revokes every refresh token the account holds, so
 * that session does not refresh, it dies and comes back through the login screen.)
 */
export function onMustChangePassword(cb: () => void): void {
  mustChangePasswordHandler = cb;
}

/** Test seam. Production code goes through setTokens()/loadTokens(). */
export function __setTokensForTest(tokens: Tokens | null): void {
  cachedTokens = tokens;
  refreshInFlight = null;
}

/** Store a freshly issued pair (login, register, refresh). */
export async function setTokens(tokens: Tokens): Promise<void> {
  cachedTokens = tokens;
  await saveTokens(tokens);
}

/** Drop the session locally. */
export async function forgetTokens(): Promise<void> {
  cachedTokens = null;
  refreshInFlight = null;
  await clearTokens();
}

async function currentTokens(): Promise<Tokens | null> {
  if (!cachedTokens) {
    cachedTokens = await loadTokens();
  }
  return cachedTokens;
}

/**
 * Refresh, but only once at a time. Every caller that arrives while a refresh is
 * running gets the same promise — nobody starts a second one.
 */
function refreshTokens(refreshToken: string): Promise<Tokens> {
  if (refreshInFlight) {
    return refreshInFlight;
  }
  refreshInFlight = doRefresh(refreshToken).finally(() => {
    refreshInFlight = null;
  });
  return refreshInFlight;
}

/**
 * Куда слать запрос: `/auth/*` — в единый SSO (`sso.sam-vision.uz`), всё
 * остальное — в контурный API. Пока `SSO_BASE_URL === API_BASE_URL` это no-op;
 * станет реальным маршрутом, когда кабинет соберут с SSO-адресом. См. spec
 * central-sso: identity общая, данные контурные.
 */
function authBase(path: string): string {
  return path.startsWith('/auth/') ? SSO_BASE_URL : API_BASE_URL;
}

async function doRefresh(refreshToken: string): Promise<Tokens> {
  // Raw fetch, not request(): a 401 here must not recurse back into refresh.
  // /auth/refresh — тоже identity, идёт в SSO.
  const res = await fetch(`${SSO_BASE_URL}/auth/refresh`, {
    method: 'POST',
    headers: {'Content-Type': 'application/json', ...sessionModeHeader()},
    ...credentialsInit(),
    // Cookie mode sends no token: the gateway reads it from the refresh cookie
    // and re-issues both cookies, since a refresh rotates the pair.
    body: JSON.stringify(SESSION_IN_COOKIES ? {} : {refresh_token: refreshToken}),
  });

  if (!res.ok) {
    // The refresh token is dead: nothing left to retry with.
    await forgetTokens();
    unauthorizedHandler?.();
    throw new ApiError(res.status, 'Session expired', await safeBody(res));
  }

  // sv_gateway AuthTokenResponse — snake_case on the wire. Read BEFORE the cookie-mode
  // return below: what that mode blanks is the token pair, not the rest of the body,
  // and `must_change_password` is exactly the part that still has to be acted on.
  const body = ((await safeBody(res)) ?? {}) as {
    access_token?: string;
    refresh_token?: string;
    must_change_password?: boolean | null;
  };

  if (body.must_change_password === true) {
    mustChangePasswordHandler?.();
  }

  // In cookie mode the gateway deliberately blanks the pair in the body — it
  // went into Set-Cookie instead, and the point is that no script ever holds
  // it. There is nothing to read or store; the refresh already succeeded.
  if (SESSION_IN_COOKIES) {
    return EMPTY_TOKENS;
  }

  const tokens: Tokens = {
    accessToken: body.access_token ?? '',
    refreshToken: body.refresh_token ?? '',
  };
  await setTokens(tokens);
  return tokens;
}

async function safeBody(res: Response): Promise<unknown> {
  try {
    return await res.json();
  } catch {
    return null;
  }
}

/** The gateway reports failures as {message} (BFF) or {error} (upstream). */
function errorMessage(body: unknown, status: number): string {
  const b = body as {message?: string; error?: string} | null;
  return b?.message ?? b?.error ?? `HTTP ${status}`;
}

/**
 * Everything above the wire, minus the body handling: auth headers, the single-flight
 * refresh, the one retry, and turning a non-2xx into an ApiError. Both `send` (JSON)
 * and the export routes (spreadsheets) come through here, so a download gets exactly
 * the same session treatment as a data call — including the refresh, which is the part
 * a hand-rolled `fetch` for a file download always forgets.
 */
async function sendRaw(
  path: string,
  init: RequestInit,
  extraHeaders: Record<string, string>,
  allowRetry: boolean,
): Promise<Response> {
  const tokens = await currentTokens();

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...sessionModeHeader(),
    ...extraHeaders,
    ...((init.headers ?? {}) as Record<string, string>),
  };
  if (tokens?.accessToken) {
    headers.Authorization = `Bearer ${tokens.accessToken}`;
  }

  // An empty value is how a caller UNSETS a default (requestControlRaw drops the JSON
  // content type on a bodyless GET). Sending the header with an empty string instead
  // is not the same thing — some proxies reject it outright.
  for (const [name, value] of Object.entries(headers)) {
    if (value === '') {
      delete headers[name];
    }
  }

  const res = await fetch(`${authBase(path)}${path}`, {
    ...init,
    headers,
    ...credentialsInit(),
  });

  // In cookie mode there is no refresh token to check for — the page cannot see
  // one. A 401 still means "try refreshing"; the browser sends the cookie.
  if (
    res.status === 401 &&
    allowRetry &&
    (SESSION_IN_COOKIES || tokens?.refreshToken)
  ) {
    // May throw (session gone) — that propagates to the caller, which is right:
    // the request cannot be completed.
    await refreshTokens(tokens?.refreshToken ?? '');
    // Retry ONCE. allowRetry=false, so a second 401 gives up instead of looping.
    return sendRaw(path, init, extraHeaders, false);
  }

  if (!res.ok) {
    const body = await safeBody(res);
    throw new ApiError(res.status, errorMessage(body, res.status), body);
  }

  return res;
}

async function send<T>(
  path: string,
  init: RequestInit,
  extraHeaders: Record<string, string>,
  allowRetry: boolean,
): Promise<T> {
  const res = await sendRaw(path, init, extraHeaders, allowRetry);

  if (res.status === 204) {
    return undefined as T;
  }
  return (await res.json()) as T;
}

export function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  return send<T>(path, init, {}, true);
}

/** sv_control endpoints: always tenant-scoped. */
export function requestControl<T>(
  path: string,
  companyId: string,
  init: RequestInit = {},
): Promise<T> {
  return send<T>(path, init, {'X-Company-Id': companyId}, true);
}

/**
 * The same wire as {@link requestControl} — `X-Company-Id` — under the name that says
 * what the header means OUTSIDE `/control/*`.
 *
 * On `/control/*` the header picks the TENANT whose rows are read. On `/roles/*` and
 * `/permissions/*` it picks the Casbin DOMAIN the caller's own policy is matched in,
 * and that distinction is not cosmetic: `company_or_system_domain` falls back to
 * `system` when the header is absent, and `system` is a domain a company owner holds
 * nothing in. Sending no header on `POST /roles/assign` is therefore not "unscoped" —
 * it is a 403 for every caller who is not a platform administrator.
 *
 * The same trap catches `sv_organization`'s `/departments/{id}` (GET/PUT/DELETE): the
 * company is in neither the path nor the body there, so the header is the only thing
 * saying which domain to match the caller in — see `entities/department/api.ts`.
 *
 * One function, two names, so a reader of `entities/role/api.ts` is not left deducing
 * why a call to sv_permissions goes through something called `requestControl`.
 */
export const requestInCompany = requestControl;

/**
 * A tenant-scoped call whose body is NOT JSON — the `/…/export` routes, which answer
 * with an .xlsx or a .csv. The caller gets the raw `Response` and decides what to do
 * with it (shared/lib/download turns it into a file on web).
 *
 * `Content-Type: application/json` is dropped: these are GETs with no body, and
 * announcing a JSON body that does not exist is the kind of thing a strict proxy
 * refuses. `Accept` is left to the caller's `init` — the route picks the format from
 * its own `?format=` parameter, not from content negotiation.
 */
export function requestControlRaw(
  path: string,
  companyId: string,
  init: RequestInit = {},
): Promise<Response> {
  return sendRaw(
    path,
    {...init, headers: {'Content-Type': '', ...((init.headers ?? {}) as object)}},
    {'X-Company-Id': companyId},
    true,
  );
}

/*
 * `authHeaders(companyId)` жил здесь и был удалён 2026-08-27 вместе со своим
 * единственным потребителем — `<Image source={{uri, headers}}>` в списке людей.
 *
 * ⚠️ Удалён НЕ потому, что не работал. В первой редакции этого комментария было
 * написано, что в вебе он бесполезен (react-native-web рисует `<Image>` как `<img>`, а
 * `<img>` пользовательских заголовков не шлёт). Первая половина верна, вывод — нет:
 * веб-сессия лежит в HttpOnly-куках, браузер прикладывает их сам, и картинка грузится.
 * Проверено на боевом гейтвее.
 *
 * Так что это чистка, а не починка: лица во всём кабинете теперь ходят одним путём
 * через клиент (`entities/personnel/avatar.ts`), и второй, полузаброшенный, убран,
 * чтобы не пришлось однажды выбирать между ними.
 */

/** /me/* endpoints: identity comes from the JWT; companyId is only a filter. */
export function requestMe<T>(
  path: string,
  companyId?: string,
  init: RequestInit = {},
): Promise<T> {
  return send<T>(path, init, companyId ? {'X-Company-Id': companyId} : {}, true);
}
